/**
 * Lighting model — a per-tile light field (0..CONFIG.light.max) driven by
 * fixtures placed in the map, switched per-room at the fusebox.
 *
 * Fixtures flood light out to their radius with a linear falloff, blocked by
 * anything opaque (full walls / void / the fusebox panel) but passing OVER
 * low cover (cubicle partitions, half walls) — a ceiling light spills across
 * a room and leaks through doorways, but not through a solid wall. Combined by
 * MAX per tile so overlapping fixtures don't blow past the ceiling.
 *
 * The field is recomputed only when a breaker toggles (not per tick). Detection
 * reads it via `visibilityFactor`: dark tiles hide the player from sight
 * (sound is unaffected). Rendering reads it to dim tiles + entities.
 *
 * Headless — no PixiJS. See CLAUDE.md architecture rule 4.
 */

import { CONFIG } from '../config';
import { blindEntity } from './blind';
import { computeVisionCone } from './fov';
import type { Guard } from './guard';
import { inBounds, tileAt, type LightSource, type MapData, type Room } from './map';
import { blocksVision, TileType } from './tiles';

const NEIGHBORS8: ReadonlyArray<readonly [number, number]> = [
  [-1, -1], [0, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [0, 1], [1, 1],
];

/**
 * Opaque to LIGHT — casts a shadow. Full walls / void / the fusebox panel
 * (blocksVision) plus HALF WALLS: waist-high cover blocks a ceiling light at
 * floor level, so the tile directly behind a half wall sits in shadow you can
 * hide in. (Cubicle partitions stay light-transparent — light spills over the
 * office panels.)
 */
function blocksLight(t: TileType): boolean {
  return blocksVision(t) || t === TileType.HalfWall;
}

/** The room containing (x, y), or null. First match wins (rooms are disjoint). */
export function roomAt(map: MapData, x: number, y: number): Room | null {
  for (const r of map.rooms) {
    if (x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1) return r;
  }
  return null;
}

/** Light level at a tile (0 outside the map). Includes any transient dynamic
 *  lights (muzzle flashes) layered over the static field. */
export function lightLevelAt(map: MapData, x: number, y: number): number {
  if (!inBounds(map, x, y)) return 0;
  const idx = y * map.width + x;
  let light = map.lightField[idx] ?? 0;
  // Guard flashlight beams (max-combined, like fixtures).
  const fl = map.flashlightField[idx] ?? 0;
  if (fl > light) light = fl;
  for (const d of map.dynamicLights) {
    if (d.ttl <= 0) continue;
    const dist = Math.hypot(d.x - x, d.y - y);
    if (dist > d.radius) continue;
    // Falloff by distance and by remaining life (fades as the flash dies).
    const contrib = d.intensity * (1 - dist / d.radius) * Math.max(0, d.ttl / d.life);
    if (contrib > light) light = contrib;
  }
  return light;
}

/** A tile is UNLIT (effectively pitch-black) when its light is at/below the
 *  dark epsilon — entities there aren't rendered, focusable, or cone-lit. */
export function isUnlit(map: MapData, x: number, y: number): boolean {
  return lightLevelAt(map, x, y) < CONFIG.light.darkEps;
}

/** Light level as PERCEIVED by a viewer, adding the night-vision boost when the
 *  viewer has NV active. Used by detection (guards with NV see in the dark) and
 *  rendering (the player's NV brightens the world). */
export function perceivedLight(map: MapData, x: number, y: number, nightVision: boolean): number {
  return lightLevelAt(map, x, y) + (nightVision ? CONFIG.light.nvBoost : 0);
}

/**
 * Sunlight brightness admitted by a window at a given hour-of-day (0..24, may be
 * fractional). Zero at night, rising to `CONFIG.light.windowIntensity` around
 * solar noon via a raised-cosine daylight bell (dawn 6:00 → dusk 19:00).
 */
export function sunlightIntensity(hour: number): number {
  const dawn = 6;
  const dusk = 19;
  if (hour <= dawn || hour >= dusk) return 0;
  const frac = (hour - dawn) / (dusk - dawn); // 0..1 across the day
  return CONFIG.light.windowIntensity * Math.sin(frac * Math.PI);
}

/**
 * Sight visibility multiplier for a target standing in `light`:
 *   ≤ hideBelow → 0 (effectively invisible — you can sneak here)
 *   ≥ fullAt    → 1 (seen normally)
 *   linear ramp between.
 */
export function visibilityFactor(light: number): number {
  const { hideBelow, fullAt } = CONFIG.light;
  if (light <= hideBelow) return 0;
  if (light >= fullAt) return 1;
  return (light - hideBelow) / (fullAt - hideBelow);
}

/**
 * Recompute `map.lightField` from the rooms + fixtures and bump `lightVersion`.
 * Every tile starts at the ambient floor; each ON fixture floods light into it.
 */
export function computeLightField(map: MapData): void {
  const field = map.lightField;
  field.fill(CONFIG.light.ambient);
  const onById = new Map<number, boolean>();
  for (const r of map.rooms) onById.set(r.id, r.lightsOn);
  for (const src of map.lights) {
    // Natural light (sunlight through a window) is NOT on the electrical grid —
    // a breaker/blackout can never switch it. Only artificial fixtures are gated.
    if (!src.natural && onById.get(src.roomId) === false) continue; // breaker off
    floodLight(map, src, field);
  }
  reflectLight(map, field);
  map.lightVersion++;
}

/**
 * Bounce pass: light reflects off surfaces to soften hard shadows, so a lit
 * room reads as lit while occluders still cast a darker (but not pitch-black)
 * region behind them. Each pass pushes a fraction of every tile's light into
 * its neighbours (through non-opaque tiles only — light doesn't bounce through
 * a wall). Iterated a couple of times for a gentle fill.
 */
function reflectLight(map: MapData, field: Float32Array): void {
  const { width, height } = map;
  const frac = CONFIG.light.reflectFraction;
  for (let pass = 0; pass < CONFIG.light.reflectPasses; pass++) {
    const src = field.slice();
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;
        if (blocksLight(tileAt(map, x, y))) continue; // don't fill solid tiles
        let best = src[idx]!;
        for (const [dx, dy] of NEIGHBORS8) {
          const nx = x + dx;
          const ny = y + dy;
          if (!inBounds(map, nx, ny)) continue;
          if (blocksLight(tileAt(map, nx, ny))) continue; // no bounce through walls
          const bounced = src[ny * width + nx]! * frac;
          if (bounced > best) best = bounced;
        }
        if (best > field[idx]!) field[idx] = best;
      }
    }
  }
}

/**
 * Direct-light raycast for one fixture. Every tile within the fixture's radius
 * that has an unobstructed line to it is lit by `intensity × (1 − dist/radius)`;
 * tiles whose line is blocked by an occluder (wall / half wall / fusebox) sit in
 * shadow (no direct light — the reflection pass fills them partially). An
 * occluder tile itself is lit on its near face (the ray to it is clear up to,
 * but not including, itself). This gives directional shadows behind walls and
 * half walls that you can hide in.
 */
function floodLight(map: MapData, src: LightSource, field: Float32Array): void {
  const { width } = map;
  const r = Math.max(1, src.radius);
  const minX = Math.max(0, src.x - r);
  const maxX = Math.min(map.width - 1, src.x + r);
  const minY = Math.max(0, src.y - r);
  const maxY = Math.min(map.height - 1, src.y + r);
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      const dist = Math.hypot(x - src.x, y - src.y);
      if (dist > r) continue;
      if (dist > 0 && lightBlockedBetween(map, src.x, src.y, x, y)) continue; // shadowed
      const lvl = src.intensity * (1 - dist / r);
      const idx = y * width + x;
      if (field[idx]! < lvl) field[idx] = lvl;
    }
  }
}

/**
 * Bresenham line from (x0,y0) to (x1,y1); returns true if any INTERIOR tile
 * (excluding both endpoints) is opaque to light. Used to decide whether a tile
 * has line-of-sight to a fixture.
 */
function lightBlockedBetween(map: MapData, x0: number, y0: number, x1: number, y1: number): boolean {
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  let x = x0;
  let y = y0;
  while (true) {
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx) { err += dx; y += sy; }
    if (x === x1 && y === y1) return false; // reached target with a clear line
    if (blocksLight(tileAt(map, x, y))) return true; // occluder between
  }
}

/**
 * Flip a room's breaker and recompute the field. Returns the new on/off state,
 * or null if the room id is unknown.
 */
export function toggleRoomLights(map: MapData, roomId: number): boolean | null {
  const room = map.rooms.find((r) => r.id === roomId);
  if (!room) return null;
  room.lightsOn = !room.lightsOn;
  computeLightField(map);
  return room.lightsOn;
}

/** True if any room's breaker is currently off. */
export function anyLightsOff(map: MapData): boolean {
  return map.rooms.some((r) => !r.lightsOn);
}

/**
 * Flip ONE off breaker back on (the first found) and recompute. Returns the room
 * that was switched on, or null if every breaker was already on. Used for the
 * staged, one-fuse-at-a-time power restore at the fusebox.
 */
export function restoreOneRoom(map: MapData): Room | null {
  const room = map.rooms.find((r) => !r.lightsOn);
  if (!room) return null;
  room.lightsOn = true;
  computeLightField(map);
  return room;
}

/** Turn every room's breaker on and recompute. Returns true if anything changed. */
export function restoreAllLights(map: MapData): boolean {
  let changed = false;
  for (const r of map.rooms) {
    if (!r.lightsOn) {
      r.lightsOn = true;
      changed = true;
    }
  }
  if (changed) computeLightField(map);
  return changed;
}

/**
 * Recompute `map.flashlightField` from guards' torches. A live, non-night-vision
 * guard whose tile is darker than `flashlightOnBelow` switches its flashlight on:
 * a narrow forward cone (`flashlightWidthFrac` of the vision half-angle) out to
 * `flashlightRange`, walls blocking the beam, linear falloff from
 * `flashlightIntensity` at the guard to 0 at the range. Tiles are max-combined so
 * overlapping beams don't stack past the source. Sets each guard's
 * `flashlightOn` for the renderer. Uses the guard's CURRENT (last-tick) facing —
 * call once per tick before the awareness pass so detection sees fresh beams.
 */
/**
 * Auto-manage NV-goggle guards (the captain). Goggles switch ON when the tile
 * goes dark (below `nvOnBelow`) and, while ON, are OVERWHELMED — flash-blinding
 * the wearer and switching OFF — the instant the tile turns bright (at/above
 * `nvBlindAt`). So: cut power → captain's goggles come on in the dark; restore
 * power while it's in a lit spot → it's blinded. Run each tick before awareness.
 */
export function updateNightVisionGoggles(guards: readonly Guard[], map: MapData): void {
  const { nvBlindAt, nvOnBelow } = CONFIG.light;
  for (const g of guards) {
    if (g.body.dead || !g.hasNightVision) continue;
    const light = lightLevelAt(map, g.x, g.y);
    if (g.nightVision) {
      if (light >= nvBlindAt) { blindEntity(g); g.nightVision = false; }
    } else if (light < nvOnBelow && g.blindTimer <= 0) {
      g.nightVision = true;
    }
  }
}

export function computeFlashlights(guards: readonly Guard[], map: MapData): void {
  const field = map.flashlightField;
  field.fill(0);
  const w = map.width;
  const range = CONFIG.light.flashlightRange;
  const halfAngle = CONFIG.vision.halfAngleRad * CONFIG.light.flashlightWidthFrac;
  const intensity = CONFIG.light.flashlightIntensity;
  for (const g of guards) {
    // Trigger off the STATIC field only (no flashlight/muzzle) to avoid feedback.
    const on = !g.body.dead && !g.nightVision && (map.lightField[g.y * w + g.x] ?? 0) < CONFIG.light.flashlightOnBelow;
    g.flashlightOn = on;
    if (!on) continue;
    computeVisionCone(
      g.x,
      g.y,
      g.facingDx,
      g.facingDy,
      range,
      halfAngle,
      (x, y) => blocksVision(tileAt(map, x, y)),
      (x, y) => {
        const dist = Math.hypot(x - g.x, y - g.y);
        const lvl = intensity * Math.max(0, 1 - dist / range);
        const i = y * w + x;
        if ((field[i] ?? 0) < lvl) field[i] = lvl;
      },
    );
  }
}

/** Add a transient muzzle-flash light at (x, y). Reveals a shooter in the dark
 *  for a fraction of a second. */
export function addMuzzleLight(map: MapData, x: number, y: number): void {
  map.dynamicLights.push({
    x,
    y,
    intensity: CONFIG.light.muzzleIntensity,
    radius: CONFIG.light.muzzleRadius,
    ttl: CONFIG.light.muzzleLifeSec,
    life: CONFIG.light.muzzleLifeSec,
  });
}

/** Age dynamic lights and drop the expired ones. Call once per tick. */
export function tickDynamicLights(map: MapData, dt: number): void {
  const arr = map.dynamicLights;
  for (const d of arr) d.ttl -= dt;
  for (let i = arr.length - 1; i >= 0; i--) if (arr[i]!.ttl <= 0) arr.splice(i, 1);
}
